import { ProductAvailability } from '~/graphql/generated';

export type ProductRating =
    | {
          '@type': 'AggregateRating';
          ratingValue: number;
          reviewCount: number;
      }
    | undefined;

export const useProductSchema = ({
    variant,
    description,
    gallery,
}: {
    variant: ComputedRef<CMSProductVariant | undefined>;
    description: string;
    gallery: ComputedRef<
        {
            src: string;
        }[]
    >;
}) => {
    const { isUS } = useLocales();
    const productRating = ref<ProductRating>();

    if (import.meta.browser && isUS.value) {
        const updateRatingData = () => {
            try {
                const ratingCount = document.querySelector<HTMLMetaElement>('meta[itemprop="reviewCount"]')?.content ?? '0';
                const ratingValue = document.querySelector<HTMLMetaElement>('meta[itemprop="ratingValue"]')?.content ?? '0';

                productRating.value = {
                    '@type': 'AggregateRating',
                    ratingValue: Number(ratingValue),
                    reviewCount: Number(ratingCount),
                };
            } catch (error) {
                console.warn('Failed to update product rating:', error);
                productRating.value = undefined;
            }
        };

        updateRatingData();

        document.addEventListener('stamped:reviews:loaded', updateRatingData);

        onUnmounted(() => {
            document.removeEventListener('stamped:reviews:loaded', updateRatingData);
        });
    }

    /** https://schema.org/ItemAvailability */
    const availabilityStatus = computed(() => {
        if (!variant.value) return 'https://schema.org/OutOfStock';

        if (variant.value.availabilityState === ProductAvailability.OutOfStock) {
            return variant.value.notifyMe ? 'https://schema.org/OutOfStock' : 'https://schema.org/Discontinued';
        }

        if (variant.value.quantityAvailable <= ProductQuantityAvailable.LOW_STOCK) {
            return 'https://schema.org/LimitedAvailability';
        }

        return 'https://schema.org/InStock';
    });

    const gallerySRC = computed(() => {
        return gallery.value.map((image) => image.src);
    });

    useJsonld(() => {
        const color = variant.value?.selectedOptions.Color;
        const size = variant.value?.selectedOptions.Size;
        const image = gallerySRC.value.length ? gallerySRC.value : variant.value?.shopImage;
        const price = variant.value?.priceNumeric ? (variant.value?.priceNumeric / 100).toFixed(2) : '0.00';

        return {
            '@context': 'https://schema.org',
            '@type': 'Product',
            description: removeTags(description),
            name: removeWoomTextFormat(variant.value?.title) || variant.value?.title,
            sku: variant.value?.sku,
            image,
            color,
            size,
            aggregateRating: productRating.value,
            offers: {
                '@type': 'Offer',
                price,
                priceCurrency: variant.value?.currency,
                availability: availabilityStatus.value,
                hasMerchantReturnPolicy: {
                    '@type': 'MerchantReturnPolicy',
                    applicableCountry: ['US', 'GB', 'DE', 'AT', 'CH', 'NL', 'FR', 'SV', 'DK'],
                    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
                    merchantReturnDays: 30,
                },
            },
            brand: { '@type': 'Brand', name: 'woom' },
        };
    });
};
